import useSWR from "swr";
import { fetcher, api } from "@/lib/api";
import KpiTile from "@/components/KpiTile";
import { PageHeader, Section } from "@/components/Heading";
import { formatIndianCurrency, formatPct, formatDate } from "@/lib/format";
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, PieChart, Pie, Cell,
} from "recharts";

const TONES = ["hsl(var(--primary))", "hsl(var(--chart-2))", "hsl(var(--chart-3))", "hsl(var(--chart-4))"];

import { usePeriod } from "@/components/PeriodFilterBar";

const LABEL = { DAY: "Today", WEEK: "Last 7 days", MTD: "Month to date",
                QTD: "Quarter to date", YTD: "Year to date", LTM: "Last 12 months" };

export default function HrAttrition() {
  const { period } = usePeriod();
  const { data } = useSWR(`/hr/attrition?period=${period}`, fetcher);
  const [brief, setBrief] = useState(null);

  useEffect(() => {
    api.get("/ai/hr-risk-brief").then((r) => setBrief(r.data.text)).catch(() => {});
  }, []);

  if (!data) return null;

  // Narrative
  const topDept = data.by_department?.[0];
  const topReason = [...(data.leaving_types || [])].sort((a, b) => b.value - a.value)[0];
  const newGrads = data.cohorts?.find((c) => c.label.includes("New"))?.count || 0;

  return (
    <div>
      <PageHeader
        eyebrow={`People · Attrition · ${LABEL[period] || period}`}
        title="Who's leaving, and why."
        lede="A 12-month look at exits, costs and cohorts — plus the last 3 months in full, no caps."
      />

      {/* Narrative strip */}
      <div className="grid md:grid-cols-3 gap-px bg-border mb-10">
        <div className="bg-background p-5">
          <div className="label-caps text-destructive mb-2">Highest bleed</div>
          <div className="font-display text-xl">{topDept?.department || "—"}</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {topDept?.exits || 0} exits against {topDept?.active || 0} active — {formatPct(topDept?.attrition_rate || 0)} attrition rate.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-warning mb-2">Why they're going</div>
          <div className="font-display text-xl">{topReason?.name || "—"}</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            {topReason?.value || 0} of last-12M exits. Run exit-interview pattern analysis on this cohort first.
          </p>
        </div>
        <div className="bg-background p-5">
          <div className="label-caps text-primary mb-2">Early-tenure risk</div>
          <div className="font-display text-xl">{newGrads} exits in first 6 months</div>
          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
            New-joiner cohort losses. Tighten onboarding and 30/60/90 check-ins to plug this.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border">
        <KpiTile testId="kpi-exits-12m" label="Exits · 12M" kind="number" value={data.summary.total_exits_12m} accent />
        <KpiTile testId="kpi-exits-3m" label="Exits · last 3M" kind="number" value={data.summary.total_exits_3m} />
        <KpiTile testId="kpi-tenure" label="Avg tenure at exit" value={`${data.summary.avg_tenure_months}m`} kind="text" />
        <KpiTile testId="kpi-attrition-cost" label="Attrition cost" value={data.summary.attrition_cost} />
      </div>

      <Section eyebrow="HR risk brief" title="What to act on">
        <div className="hairline p-5 flex gap-4">
          <Sparkles className="w-5 h-5 text-primary mt-1 shrink-0" />
          <div>
            <p className="text-base leading-relaxed" data-testid="hr-risk-brief-text">{brief || "Drafting brief…"}</p>
            <div className="label-caps mt-3">Claude Sonnet 4.5 · refreshes every 6h</div>
          </div>
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-10 mt-14">
        <Section eyebrow="Monthly trend" title="Exit curve · 12 months">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.monthly_trend}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Line type="monotone" dataKey="exits" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section eyebrow="Tenure at exit" title="How long they stayed">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={data.tenure_distribution}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="bucket" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section eyebrow="Reason" title="Leaving type">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.leaving_types} dataKey="value" innerRadius={60} outerRadius={100} stroke="hsl(var(--background))" strokeWidth={3}>
                  {data.leaving_types.map((_, i) => <Cell key={i} fill={TONES[i]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))" }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-around mt-3">
              {data.leaving_types.map((t, i) => (
                <div key={t.name} className="text-center">
                  <div className="w-3 h-3 mx-auto" style={{ background: TONES[i] }} />
                  <div className="label-caps mt-1">{t.name}</div>
                  <div className="font-mono text-sm">{t.value}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>
        <Section eyebrow="Survival curve" title="% surviving at each tenure">
          <div className="hairline p-4">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.survival_curve}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="2 4" />
                <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={10} tickFormatter={(v) => `${v}m`} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={[0, 100]} />
                <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", fontSize: 12 }} formatter={(v) => `${v}%`} />
                <Line type="monotone" dataKey="survival_pct" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <Section eyebrow={`Last 3 months · ${data.recent_exits.length} exits`} title="Recent exits">
        <div className="hidden md:block hairline">
          <table className="w-full table-dense">
            <thead>
              <tr>
                <th>Name</th>
                <th>Department</th>
                <th>Type</th>
                <th>Leaving</th>
                <th>Exit date</th>
                <th className="text-right">Tenure</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_exits.map((e) => (
                <tr key={e.emp_id} data-testid="recent-exit-row">
                  <td className="font-display text-sm">{e.name}</td>
                  <td className="text-muted-foreground">{e.department}</td>
                  <td><span className="hairline px-2 py-0.5 text-[10px] uppercase tracking-widest">{e.employee_type}</span></td>
                  <td>{e.leaving_type}</td>
                  <td className="text-destructive">{formatDate(e.resign_date)}</td>
                  <td className="text-right">{e.tenure_months}m</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="md:hidden space-y-2">
          {data.recent_exits.map((e) => (
            <div key={e.emp_id} className="hairline p-3">
              <div className="flex justify-between">
                <div className="font-display text-sm">{e.name}</div>
                <div className="font-mono text-xs text-destructive">{formatDate(e.resign_date)}</div>
              </div>
              <div className="text-[11px] text-muted-foreground">{e.department} · {e.leaving_type} · {e.tenure_months}m</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
